import { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { ROUTE_PREFIX } from '@link/agent/constants';
import { agentAccountApi } from '@link/agent/services';
import { useStore } from '@link/agent/stores';
import { INBOX_ENABLED } from '@link/share/constants';
import {
  BookingIcon,
  OtaIcon,
  // ContractIcon,
  SettingsIcon,
  PaymentIcon,
  BulkOrderIcon,
  NoteIcon,
  OrderIcon
} from '@link/share/icons';
import { SidebarMenu, bottomItems } from '@link/share/layouts';
import { AccountbaseStatus, PaymentType } from '@link/share/services/const';

const Sidebar = () => {
  const { t } = useTranslation();
  const { agent, auth } = useStore();
  const [agentOrgsTotal, setAgentOrgsTotal] = useState(0);
  const menuItems = useMemo(() => {
    return [
      //removed contracts list menu, will be added back when needed
      // ...(auth.canOperate
      //   ? [
      //       {
      //         label: t('layouts.sidebar.contractsList'),
      //         Icon: ContractIcon,
      //         name: 'contractsList',
      //         to: '/contracts/list'
      //       }
      //     ]
      //   : []),
      {
        label: t('layouts.sidebar.agents'),
        Icon: OtaIcon,
        name: 'agent',
        children: [
          {
            label: t('views.agentDetail.title'),
            to: '/agent/detail'
          },
          ...(auth.canOperate && agentOrgsTotal > 1
            ? [
                {
                  label: t('views.orgList.title'),
                  to: '/agent/orgs'
                }
              ]
            : []),
          ...(auth.canOperate
            ? [
                {
                  label: t('views.agentAccountList.title'),
                  to: '/agent/accounts'
                }
              ]
            : [])
        ]
      },
      {
        label: t('layouts.sidebar.activityList'),
        Icon: OrderIcon,
        name: 'activities',
        to: '/activities'
      },
      {
        label: t('layouts.sidebar.booking'),
        Icon: BookingIcon,
        name: 'book',
        children: [
          {
            label: t('views.searchActivities.title'),
            to: ROUTE_PREFIX.BOOKING
          },
          {
            label: t('share.views.bookingList.title'),
            to: '/book/booking'
          },
          {
            label: t('views.reservations.title'),
            to: '/book/reservations'
          }
        ]
      },
      ...(auth.canOperate
        ? [
            {
              label: t('layouts.sidebar.bulkOrder'),
              Icon: BulkOrderIcon,
              name: 'bulkOrder',
              children: [
                {
                  label: t('views.searchActivities.title'),
                  to: ROUTE_PREFIX.BULK_ORDER
                },
                {
                  label: t('share.views.bookingList.title'),
                  to: '/bulk-order/list'
                }
              ]
            }
          ]
        : []),
      ...(auth.isAdmin || auth.isLinkAdmin
        ? [
            {
              label: t('layouts.sidebar.finance'),
              Icon: PaymentIcon,
              name: 'finance',
              children: [
                {
                  label: t('views.financeHistory.title'),
                  to: '/finance/history'
                },
                {
                  label: t('views.financeTransactions.title'),
                  to: '/finance/transactions'
                },
                {
                  label: t('share.views.discrepancyList.title'),
                  to: '/finance/discrepancies'
                },
                //後払い(銀行振込)、前払い(linkpay)、後払い(linkpay)、前払い（残高チャージ）
                //后付的不展示
                ...(agent.paymentType === PaymentType.PreLinkpay ||
                agent.paymentType === PaymentType.PreCharge
                  ? [
                      {
                        label: t('views.financeAccount.title'),
                        to: '/finance/account'
                      }
                    ]
                  : [])
              ]
            }
          ]
        : []),
      ...(INBOX_ENABLED
        ? [
            {
              label: t('layouts.sidebar.notifications'),
              name: 'notifications',
              Icon: NoteIcon,
              children: [
                {
                  label: t('share.views.notificationInbox.title'),
                  to: '/notifications/inbox'
                }
              ]
            }
          ]
        : []),
      ...(auth.isPasswordLogin
        ? [
            {
              label: t('layouts.sidebar.settings'),
              name: 'settings',
              Icon: SettingsIcon,
              children: [
                {
                  label: t('layouts.sidebar.changePassword'),
                  to: '/change-password'
                }
              ]
            }
          ]
        : [])
    ];
  }, [
    t,
    auth.canOperate,
    auth.isAdmin,
    auth.isLinkAdmin,
    agentOrgsTotal,
    agent.paymentType,
    auth.isPasswordLogin
  ]);

  useEffect(() => {
    if (auth.loading || auth.isViewer) return;
    if (agent.selectedAgentId) {
      agentAccountApi
        .listOtaOrgsWithPage({
          otaId: agent.selectedAgentId,
          status: [AccountbaseStatus.On]
        })
        .then(res => {
          if (res.total) setAgentOrgsTotal(res.total);
        });
    }
  }, [agent.selectedAgentId, auth.loading, auth.isViewer]);

  const extendedBottomItems = [
    {
      label: t('share.common.manual'),
      to: 'https://sites.google.com/linktivity.co.jp/agent-gds-neo/%E3%83%9B%E3%83%BC%E3%83%A0'
    },
    ...bottomItems
  ];

  useEffect(() => {
    if (auth.loading) return;
    if (auth.isLinkAdmin && agent.selectedAgentId) {
      agent.getOtaOrgs(agent.selectedAgentId);
    }
  }, [agent.selectedAgentId, agent, auth.loading, auth.isLinkAdmin]);

  return (
    <SidebarMenu menuItems={menuItems} bottomItems={extendedBottomItems} />
  );
};

const SidebarView = observer(Sidebar);
export default SidebarView;
